import { Box, BoxProps, SxProps, useTheme } from '@mui/material';
import ModulesSearch from './ModulesSearch';
import { Module, ModuleID, ModuleTreeObject } from '../types';
import {
    FC,
    MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    Dispatch,
    SetStateAction,
    JSXElementConstructor,
    SyntheticEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectGetModulesStatus, selectModules } from '../store/selectors';
import { isMac, REQUEST_STATUS } from '@shared/constants/otherConstants';
import { ALL_MODULES_TREE_ITEM_ID } from '../constants/modulesConstants';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { RichTreeView, treeItemClasses, useTreeViewApiRef } from '@mui/x-tree-view';
import CustomTreeItem from './CustomTreeItem';
import { TreeItem2Props } from '@mui/x-tree-view/TreeItem2';
import {
    findModuleChildrenById,
    getModuleParentPathByModuleId,
    getModulesTreeView,
} from '@pages/modules/utils/modulesUtils';

export interface ModulesTreeViewProps {
    selectedModules: ModuleID[];
    setSelectedModules: Dispatch<SetStateAction<ModuleID[]>>;
    searchString: string;
    setSearchString: Dispatch<SetStateAction<string>>;
    expandedModules: ModuleID[];
    setExpandedModules: Dispatch<SetStateAction<ModuleID[]>>;
    RootProps?: BoxProps;
    ToolbarStyle?: SxProps;
    multiSelect?: boolean;
    disableKeyboardExpansion?: boolean;
    moduleToIgnoreId?: ModuleID;
    handleKeyDown?: (e: any) => void;
    withImplicitRoot?: boolean;
}

const ModulesTreeView: FC<ModulesTreeViewProps> = ({
    selectedModules,
    setSelectedModules,
    searchString,
    setSearchString,
    expandedModules,
    setExpandedModules,
    RootProps,
    ToolbarStyle,
    multiSelect,
    disableKeyboardExpansion,
    moduleToIgnoreId,
    handleKeyDown,
    withImplicitRoot,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const modules = useSelector(selectModules);
    const getModulesStatus = useSelector(selectGetModulesStatus);
    const apiRef = useTreeViewApiRef();

    const stopClickPropagation = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    const handleClearSearchField = () => {
        setSearchString('');
    };

    // if module is ignored, ignore its children
    const moduleToIgnoreWithChildren = useMemo(() => {
        if (!moduleToIgnoreId || getModulesStatus !== REQUEST_STATUS.SUCCESS) {
            return [];
        }

        return [moduleToIgnoreId, ...findModuleChildrenById(moduleToIgnoreId, modules).map((object) => object.pk)];
    }, [getModulesStatus, moduleToIgnoreId, modules]);

    const modulesTreeView = useMemo(() => {
        let filteredModulesArray;

        if (searchString) {
            const filteredModulesSet = modules.reduce(
                (filteredModules: Set<Module>, module: Module, _index: number, modules: Module[]) => {
                    if (module.name.toLowerCase().includes(searchString.toLowerCase())) {
                        const moduleObjectsPathArray = getModuleParentPathByModuleId(module.pk, modules);
                        moduleObjectsPathArray.forEach((module: Module) => filteredModules.add(module));
                    }

                    return filteredModules;
                },
                new Set(),
            );

            filteredModulesArray = Array.from(filteredModulesSet);
        } else {
            filteredModulesArray = modules;
        }

        const filteredModulesArrayWithoutIgnored = filteredModulesArray.filter(
            (module: Module) => !moduleToIgnoreWithChildren.includes(module.pk),
        );

        return getModulesTreeView(filteredModulesArrayWithoutIgnored);
    }, [modules, searchString, moduleToIgnoreWithChildren]);

    const modulesTreeViewWithRootModulesItem = useMemo(() => {
        if (!withImplicitRoot) {
            return modulesTreeView;
        }

        return [
            {
                pk: ALL_MODULES_TREE_ITEM_ID,
                name: 'All modules',
                children: modulesTreeView,
            },
        ];
    }, [modulesTreeView, withImplicitRoot]);

    // when searched modules are shown, expand all of them
    useEffect(() => {
        const extractIds = (obj: ModuleTreeObject, result: ModuleID[] = []) => {
            result.push(String(obj.pk));

            if (obj.children.length > 0) {
                obj.children.forEach((child) => extractIds(child, result));
            }

            return result;
        };

        if (searchString && modulesTreeViewWithRootModulesItem.length) {
            const resultExpandedItems: ModuleID[] = [];

            for (const rootItem of modulesTreeViewWithRootModulesItem) {
                const newExpandedModules = extractIds(rootItem);
                resultExpandedItems.push(...newExpandedModules);
            }

            setExpandedModules(Array.from(new Set(resultExpandedItems)));
        }
    }, [modulesTreeViewWithRootModulesItem, searchString, setExpandedModules]);

    // expand path to selected items
    useEffect(() => {
        if (getModulesStatus !== REQUEST_STATUS.SUCCESS) {
            return;
        }

        let array: ModuleID[] = [];

        for (const selectedModule of selectedModules) {
            if (selectedModule === ALL_MODULES_TREE_ITEM_ID) {
                continue;
            }

            const path = getModuleParentPathByModuleId(selectedModule, modules, 'pk');
            path.shift();
            array = array.concat(path.map((id: ModuleID) => String(id)));
        }

        setExpandedModules((prevExpandedModules: ModuleID[]) => [
            ...prevExpandedModules,
            ...Array.from(new Set(array)),
        ]);
    }, [getModulesStatus, modules, selectedModules, setExpandedModules]);

    // navigate to module's path to make it selected
    const handleItemSelectionToggle = useCallback(
        (_event: SyntheticEvent, itemId: string, isSelected: boolean) => {
            const itemIdToToggle = itemId === ALL_MODULES_TREE_ITEM_ID ? itemId : Number(itemId);

            if (isSelected) {
                setSelectedModules((currentSelectedModules) =>
                    multiSelect ? Array.from(new Set([...currentSelectedModules, itemIdToToggle])) : [itemIdToToggle],
                );
            } else {
                setSelectedModules((currentSelectedModules) =>
                    currentSelectedModules.filter((v) => v !== itemIdToToggle),
                );
            }
        },
        [setSelectedModules, multiSelect],
    );

    const handleItemExpansionToggle = useCallback(
        (event: any, itemId: string, isExpanded: boolean) => {
            const withCtrlKey = isMac ? event.metaKey : event.ctrlKey;
            const withShiftKey = event.shiftKey;

            if ((event.type === 'keydown' && disableKeyboardExpansion) || withCtrlKey || withShiftKey) {
                return;
            }

            setExpandedModules((items) => (isExpanded ? [...items, itemId] : items.filter((item) => item !== itemId)));
        },
        [setExpandedModules, disableKeyboardExpansion],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }} onClick={stopClickPropagation} {...RootProps}>
            <Box
                sx={{
                    position: 'sticky',
                    top: '8px',
                    backgroundColor: theme.palette.background.paper,
                    zIndex: 1,
                    mt: '1px',
                    ...ToolbarStyle,
                }}
            >
                <ModulesSearch searchString={searchString} setSearchString={setSearchString} />
            </Box>
            {getModulesStatus === REQUEST_STATUS.LOADING && (
                <SimpleTreeView disableSelection expandedItems={['root-skeleton']}>
                    <TreeItem itemId="root-skeleton" label={<Skeleton />}>
                        <TreeItem itemId="child-skeleton-1" label={<Skeleton />} />
                        <TreeItem itemId="child-skeleton-2" label={<Skeleton />} />
                        <TreeItem itemId="child-skeleton-3" label={<Skeleton />} />
                    </TreeItem>
                    <TreeItem itemId="root-skeleton-2" label={<Skeleton />}>
                        <TreeItem itemId="child-skeleton-4" label={<Skeleton />} />
                    </TreeItem>
                </SimpleTreeView>
            )}
            {searchString &&
                ((withImplicitRoot && !modulesTreeViewWithRootModulesItem[0].children.length) ||
                    (!withImplicitRoot && !modulesTreeViewWithRootModulesItem.length)) && (
                    <Box>
                        <Typography component="span">{`${t('no_items_found')}. `}</Typography>
                        <Typography
                            sx={{ cursor: 'pointer' }}
                            component="span"
                            color="secondary"
                            onClick={handleClearSearchField}
                        >
                            {t('clear_filter')}
                        </Typography>
                    </Box>
                )}
            {getModulesStatus === REQUEST_STATUS.SUCCESS && (
                <RichTreeView
                    apiRef={apiRef}
                    expandedItems={expandedModules.map((m) => String(m))}
                    checkboxSelection={multiSelect}
                    multiSelect={multiSelect}
                    sx={{
                        userSelect: 'none',
                        flex: 1,
                        width: '100%',
                        overflowX: 'auto',
                        [`& .${treeItemClasses.iconContainer}`]: {
                            width: 'min-content',
                        },
                        [`& .${treeItemClasses.content}`]: {
                            minWidth: '100%',
                            width: 'max-content',
                            whiteSpace: 'nowrap',
                            gap: 0.25,
                        },
                        [`& .${treeItemClasses.content}.${treeItemClasses.selected}`]: {
                            background: 'rgba(var(--primary-light-color-rgb), 0.12)',
                        },
                        [`& .${treeItemClasses.content}.${treeItemClasses.focused}`]: {
                            background: 'rgba(var(--primary-light-color-rgb), 0.2)',
                        },
                        [`& .${treeItemClasses.content}:hover`]: {
                            background: 'rgba(var(--primary-light-color-rgb), 0.08)',
                        },
                        [`& .${treeItemClasses.content}.${treeItemClasses.selected}:hover`]: {
                            background: 'rgba(var(--primary-light-color-rgb), 0.16)',
                        },
                    }}
                    slots={{
                        item: CustomTreeItem as JSXElementConstructor<TreeItem2Props>,
                    }}
                    slotProps={{
                        item: {
                            apiRef: apiRef,
                            setExpandedModules: setExpandedModules,
                            setSelectedModules: setSelectedModules,
                            searchString,
                        } as any,
                    }}
                    items={
                        (withImplicitRoot && modulesTreeViewWithRootModulesItem[0].children.length) ||
                        (!withImplicitRoot && modulesTreeViewWithRootModulesItem.length)
                            ? modulesTreeViewWithRootModulesItem
                            : []
                    }
                    getItemId={(item) => String(item.pk)}
                    getItemLabel={(item) => item.name}
                    onItemSelectionToggle={handleItemSelectionToggle}
                    onItemExpansionToggle={handleItemExpansionToggle}
                    onKeyDown={handleKeyDown}
                    selectedItems={selectedModules.map((m) => String(m))}
                    expansionTrigger="iconContainer"
                />
            )}
        </Box>
    );
};

export default ModulesTreeView;
